/**
 * ManualSignModal — Erfassung von per E-Mail/Post/Fax eingegangenen Signaturen.
 *
 * Nutzer-Anforderung Novatus (2026-08-15):
 *   B (mit Beweis-Upload) + 1a (RBAC Rep/Director/Admin) + 2c (Löschen nur
 *   Admin) + 3c (interne Info an Sales-Director) + 4a (in bestehende
 *   "signiert"-Metriken einbeziehen).
 */
import { useMemo, useState } from "react";
import { Loader2, Upload, X, Mail, PenLine } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { InlineSignaturePad } from "@/components/InlineSignaturePad";

const SOURCE_OPTIONS = [
  { value: "email", label: "E-Mail (schriftliche Zustimmung)" },
  { value: "post", label: "Post-Brief (unterschrieben)" },
  { value: "fax", label: "Fax" },
  { value: "in_person", label: "Persönlich vor Ort" },
  { value: "phone_confirmed", label: "Telefonisch bestätigt" },
];

const MAX_FILES = 3;
const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = (dataUrl || "").split(",", 2)[1] || "";
      resolve(b64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ManualSignModal({ offerId, offerNumber, open, onClose, onSaved }) {
  const [signerName, setSignerName] = useState("");
  // Iter88zn: NICHT mehr mit "jetzt" vorbelegen — sonst tippt der Rep aus
  // Versehen den Erfassungs- statt den Zustimmungszeitpunkt.
  const [signedAt, setSignedAt] = useState("");
  const [source, setSource] = useState("email");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  // Iter88zap — optionale Signaturbilder: Haupt-Unterschrift + 2 Zusatz-
  // Erklärungen (Erlöschen · Vorzeitiger Beginn). Wenn nicht gesetzt bleibt
  // im PDF die Textform-Bestätigung stehen (§ 126b BGB).
  const [sigMain, setSigMain] = useState(null);
  const [sigErloeschen, setSigErloeschen] = useState(null);
  const [sigVorzeitig, setSigVorzeitig] = useState(null);
  const [showSigPads, setShowSigPads] = useState(false);

  const reset = () => {
    setSignerName("");
    setSignedAt("");
    setSource("email");
    setNotes("");
    setFiles([]);
    setSigMain(null);
    setSigErloeschen(null);
    setSigVorzeitig(null);
    setShowSigPads(false);
  };

  const canSave = useMemo(
    () => signerName.trim().length >= 2
      && signedAt
      && !Number.isNaN(new Date(signedAt).getTime())
      && SOURCE_OPTIONS.find(o => o.value === source),
    [signerName, signedAt, source],
  );

  const handleFileAdd = (e) => {
    const picked = Array.from(e.target.files || []);
    const remaining = MAX_FILES - files.length;
    if (picked.length > remaining) {
      toast.error(`Maximal ${MAX_FILES} Dateien erlaubt (${remaining} noch frei)`);
      return;
    }
    for (const f of picked) {
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`"${f.name}" ist größer als 10 MB`);
        return;
      }
    }
    setFiles([...files, ...picked]);
    e.target.value = "";
  };

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      const evidence = [];
      for (const f of files) {
        const b64 = await fileToBase64(f);
        evidence.push({ filename: f.name, mime: f.type || "application/octet-stream", content_base64: b64 });
      }
      const body = {
        signer_name: signerName.trim(),
        signed_at: new Date(signedAt).toISOString(),
        source,
        notes: notes.trim() || null,
        evidence_files: evidence,
        claim_empfehlungsbonus: false,
        signature_data_url: sigMain || null,
        signature_erloeschen_data_url: sigErloeschen || null,
        signature_vorzeitiger_beginn_data_url: sigVorzeitig || null,
      };
      const { data } = await api.post(`/offers/${offerId}/manual-sign`, body);
      toast.success(
        `Signatur erfasst — ${data.evidence_files_count} Beleg${data.evidence_files_count === 1 ? "" : "e"} gespeichert`,
      );
      reset();
      onSaved?.(data);
      onClose?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Speichern fehlgeschlagen";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent
        className="max-w-lg bg-zinc-900 border-zinc-800 text-zinc-100"
        data-testid="manual-sign-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Mail size={20} className="text-blue-400" />
            Manuelle Signatur erfassen
            <span className="text-xs text-zinc-500 font-normal ml-auto">{offerNumber}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div>
            <label className="block text-xs text-zinc-400 mb-1">Unterzeichner (Kundenname) *</label>
            <input
              type="text"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Max Mustermann"
              className="w-full h-11 rounded-lg bg-zinc-800 border border-zinc-700 px-3 focus:border-blue-500 outline-none"
              data-testid="manual-sign-signer-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">
                Datum &amp; Uhrzeit der Kunden-Zustimmung *
              </label>
              <input
                type="datetime-local"
                value={signedAt}
                onChange={(e) => setSignedAt(e.target.value)}
                max={new Date().toISOString().slice(0, 16)}
                required
                className="w-full h-11 rounded-lg bg-zinc-800 border border-zinc-700 px-3 focus:border-blue-500 outline-none"
                data-testid="manual-sign-signed-at"
              />
              <div className="mt-1 text-[10.5px] text-zinc-500">
                Wann kam die Zustimmung beim Kunden zustande — nicht wann Sie sie eintragen.
              </div>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Quelle *</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="w-full h-11 rounded-lg bg-zinc-800 border border-zinc-700 px-3 focus:border-blue-500 outline-none"
                data-testid="manual-sign-source"
              >
                {SOURCE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1">Notiz (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Z. B. Zustimmung im Mailtext vom 14.08. — Zitat: 'Wir nehmen das Angebot an.'"
              rows={3}
              className="w-full rounded-lg bg-zinc-800 border border-zinc-700 px-3 py-2 focus:border-blue-500 outline-none"
              data-testid="manual-sign-notes"
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1">
              Beweismittel (bis {MAX_FILES} Dateien, max. 10 MB je Datei)
            </label>
            <label
              className="w-full h-11 rounded-lg bg-zinc-800 border border-dashed border-zinc-600 flex items-center justify-center gap-2 cursor-pointer hover:border-blue-500 transition"
              data-testid="manual-sign-upload-trigger"
            >
              <Upload size={16} /> PDF / EML / Bild auswählen
              <input
                type="file"
                accept=".pdf,.eml,image/*"
                multiple
                onChange={handleFileAdd}
                className="hidden"
                disabled={files.length >= MAX_FILES}
                data-testid="manual-sign-upload-input"
              />
            </label>
            {files.length > 0 && (
              <ul className="mt-2 space-y-1">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center gap-2 text-xs bg-zinc-800 rounded px-2 py-1.5"
                  >
                    <span className="flex-1 truncate">{f.name}</span>
                    <span className="text-zinc-500">{(f.size / 1024).toFixed(0)} KB</span>
                    <button
                      onClick={() => setFiles(files.filter((_, j) => j !== i))}
                      className="text-zinc-500 hover:text-red-400"
                      data-testid={`manual-sign-remove-file-${i}`}
                    >
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <button
              type="button"
              onClick={() => setShowSigPads(!showSigPads)}
              className="w-full h-10 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-200 hover:bg-zinc-700 text-[13px] font-semibold inline-flex items-center justify-center gap-2"
              data-testid="manual-sign-toggle-pads"
            >
              <PenLine size={14} />
              {showSigPads ? "Signaturbilder ausblenden" : "Signaturbilder nachtragen (optional)"}
            </button>
            {showSigPads && (
              <div className="mt-3 space-y-3">
                <p className="text-[11.5px] text-zinc-500">
                  Wenn dir eine unterschriebene Vorlage vom Kunden vorliegt, kannst du seine Unterschrift hier nachzeichnen. Die Bilder landen im PDF an den passenden Stellen.
                </p>
                <div>
                  <div className="text-[11px] font-semibold text-zinc-300 mb-1">Haupt-Unterschrift (§18 AGB)</div>
                  <InlineSignaturePad onChange={setSigMain} height={100} testId="manual-sign-pad-main" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-zinc-300 mb-1">Erlöschen des Widerrufsrechts</div>
                  <InlineSignaturePad onChange={setSigErloeschen} height={100} testId="manual-sign-pad-erloeschen" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold text-zinc-300 mb-1">Vorzeitiger Beginn</div>
                  <InlineSignaturePad onChange={setSigVorzeitig} height={100} testId="manual-sign-pad-vorzeitig" />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={() => { reset(); onClose?.(); }}
              className="flex-1 h-11 rounded-lg bg-zinc-800 border border-zinc-700 text-zinc-300 hover:bg-zinc-700 font-semibold"
              data-testid="manual-sign-cancel"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave || saving}
              className="flex-1 h-11 rounded-lg bg-blue-500 hover:bg-blue-400 text-white font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="manual-sign-save"
            >
              {saving ? <><Loader2 className="animate-spin" size={16} /> Speichere…</> : "Signatur speichern"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
